import React, { useState, useEffect, useRef, useContext, useMemo } from 'react';
import AppContext from 'context/Context';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle, faPlus } from '@fortawesome/free-solid-svg-icons';
import useToken from 'hooks/useToken';
import { useToast } from 'context/providers/ToastProvider';

import AutomaticMessagesHeader from './componentes/AutomaticMessagesHeader';
import AutomaticMessagesFilters from './componentes/AutomaticMessagesFilters';
import AutomaticMessageCard from './componentes/AutomaticMessageCard';
import AutomaticMessageModal from './componentes/AutomaticMessageModal';
import AutomaticMessagePreviewModal from './componentes/AutomaticMessagePreviewModal';

const AutomaticMessagesSettingsSection = () => {
    const { config: { isDark } = {} } = useContext(AppContext);
    const { token, nombreCompleto } = useToken();
    const { showSuccessToast, showErrorToast } = useToast();

    const [messages, setMessages] = useState([]);
    const [agents, setAgents] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterType, setFilterType] = useState('all'); // 'all' | 'general' | 'personal'
    const [copiedId, setCopiedId] = useState(null);
    const [selectedMessageForPreview, setSelectedMessageForPreview] = useState(null);
    const [editingMessage, setEditingMessage] = useState(null);
    const isInitialMount = useRef(true);

    // Cargar catálogo de mensajes automáticos desde el backend
    const fetchMessages = async (searchQuery = searchTerm) => {
        setIsLoading(true);
        try {
            const params = new URLSearchParams();
            if (searchQuery && searchQuery.trim()) params.append('searchTerm', searchQuery.trim());

            const query = params.toString() ? `?${params.toString()}` : '';
            const response = await fetch(`/api/SupportEngine/GetMensajesAutomaticos${query}`, {
                headers: {
                    'Accept': 'application/json',
                    'Authorization': 'Bearer ' + token
                }
            });

            if (response.ok) {
                const result = await response.json();
                const list = result.data || result;
                if (Array.isArray(list)) {
                    setMessages(list);
                } else {
                    setMessages([]);
                }
            } else {
                if (showErrorToast) {
                    showErrorToast('Error al cargar la lista de mensajes automáticos.');
                }
            }
        } catch (err) {
            console.error('Error al cargar mensajes automáticos:', err);
            if (showErrorToast) {
                showErrorToast('Error de conexión al cargar mensajes automáticos.');
            }
        } finally {
            setIsLoading(false);
        }
    };

    // Cargar lista de asesores activos para el DDL de asignación
    const fetchAgents = async () => {
        try {
            const response = await fetch('/api/SupportEngine/GetAgentsList', {
                headers: {
                    'Accept': 'application/json',
                    'Authorization': 'Bearer ' + token
                }
            });

            if (response.ok) {
                const result = await response.json();
                const list = result.data || (Array.isArray(result) ? result : []);
                setAgents(Array.isArray(list) ? list : []);
            }
        } catch (err) {
            console.error('Error al cargar lista de asesores:', err);
        }
    };

    // Carga inicial de mensajes y asesores
    useEffect(() => {
        if (!token) return;

        if (isInitialMount.current) {
            isInitialMount.current = false;
            fetchMessages(searchTerm);
            fetchAgents();
            return;
        }

        setIsLoading(true);
        const timer = setTimeout(() => {
            fetchMessages(searchTerm);
        }, 300);

        return () => clearTimeout(timer);
    }, [searchTerm, token]);

    // Agrupar o listar categorías
    const groupedCategories = useMemo(() => {
        const groups = new Map();

        messages.forEach(item => {
            if (!item.vClaveMensaje || !item.vClaveMensaje.trim()) return;
            const key = item.vClaveMensaje.trim().toUpperCase();
            if (!groups.has(key)) {
                groups.set(key, {
                    idCatMensajesAutomaticos: item.idCatMensajesAutomaticos || 0,
                    clave: key,
                    vClaveMensaje: item.vClaveMensaje.trim(),
                    originalClave: item.vClaveMensaje.trim(),
                    messages: item.vMensajePersonalizado !== undefined ? [item] : []
                });
            } else if (item.vMensajePersonalizado !== undefined) {
                groups.get(key).messages.push(item);
            }
        });

        return Array.from(groups.values());
    }, [messages]);


    // Categorías filtradas por búsqueda
    const filteredCategories = useMemo(() => {
        return groupedCategories.filter(cat => {
            if (!searchTerm || !searchTerm.trim()) return true;
            return cat.vClaveMensaje.toLowerCase().includes(searchTerm.trim().toLowerCase());
        });
    }, [groupedCategories, searchTerm]);

    // Conteos para filtros
    const countGeneral = useMemo(() => {
        return groupedCategories.reduce((acc, cat) => acc + (cat.messages?.filter(m => !m.idUsuario)?.length || 0), 0);
    }, [groupedCategories]);

    const countPersonal = useMemo(() => {
        return groupedCategories.reduce((acc, cat) => acc + (cat.messages?.filter(m => !!m.idUsuario)?.length || 0), 0);
    }, [groupedCategories]);

    // Guardar (Crear nueva categoría/mensaje o Actualizar existente)
    const handleSaveMessage = async savePayload => {
        if (!editingMessage) return;

        if (!editingMessage.vClaveMensaje || !editingMessage.vClaveMensaje.trim()) {
            if (showErrorToast) showErrorToast('Debes ingresar la categoría o clave del mensaje.');
            return;
        }

        const categoryKey = editingMessage.vClaveMensaje.trim().toUpperCase();

        // Normalizar a array de items a procesar
        const itemsToSave = Array.isArray(savePayload)
            ? savePayload
            : [
                  {
                      idUsuario: savePayload?.idUsuario !== undefined ? savePayload.idUsuario : (editingMessage.idUsuario || null),
                      text: savePayload?.text ?? editingMessage.vMensajePersonalizado ?? ''
                  }
              ];

        const hasEmptyText = itemsToSave.some(item => !item.text || !item.text.trim());
        if (hasEmptyText) {
            if (showErrorToast) showErrorToast('El contenido del mensaje no puede estar vacío.');
            return;
        }

        setIsSaving(true);
        try {
            const payload = {
                idMensaje: 0,
                vClaveMensaje: categoryKey,
                vJsonMensajes: JSON.stringify(itemsToSave.map(item => ({
                    idUsuario: item.idUsuario,
                    vMensajePersonalizado: item.text.trim()
                }))),
                vCreatedUser: nombreCompleto || 'Usuario',
                vUpdatedUser: nombreCompleto || 'Usuario'
            };

            const response = await fetch('/api/SupportEngine/CreateMensajeAutomatico', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'Authorization': 'Bearer ' + token
                },
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                if (showSuccessToast) {
                    showSuccessToast('Mensajes automáticos guardados con éxito.');
                }
                setEditingMessage(null);
                fetchMessages(searchTerm);
            } else {
                if (showErrorToast) showErrorToast('Error al guardar los mensajes automáticos.');
            }
        } catch (err) {
            console.error('Error al guardar mensaje:', err);
            if (showErrorToast) {
                showErrorToast('Error de conexión al guardar el mensaje.');
            }
        } finally {
            setIsSaving(false);
        }
    };

    // Copiar mensaje al portapapeles
    const handleCopyText = (id, text) => {
        if (!text) return;
        navigator.clipboard.writeText(text);
        setCopiedId(id);
        if (showSuccessToast) {
            showSuccessToast('Mensaje copiado al portapapeles.');
        }
        setTimeout(() => {
            setCopiedId(null);
        }, 2000);
    };

    // Eliminar mensaje automático
    const handleDeleteMessage = async item => {
        if (!item || !item.idMensaje) return;

        const targetLabel = item.idUsuario
            ? `asignado a ${item.vNombreUsuario || `Usuario #${item.idUsuario}`}`
            : 'Global';

        const confirmMsg = `¿Estás seguro de que deseas eliminar el mensaje "${item.vClaveMensaje}" (${targetLabel})?`;
        if (!window.confirm(confirmMsg)) return;

        try {
            const response = await fetch('/api/SupportEngine/DeleteMensajeAutomatico', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'Authorization': 'Bearer ' + token
                },
                body: JSON.stringify({
                    idMensaje: item.idMensaje,
                    vUpdatedUser: nombreCompleto || 'Usuario'
                })
            });

            if (response.ok) {
                if (showSuccessToast) {
                    showSuccessToast('Mensaje automático eliminado con éxito.');
                }
                fetchMessages(searchTerm);
            } else {
                if (showErrorToast) {
                    showErrorToast('Error al eliminar el mensaje automático.');
                }
            }
        } catch (err) {
            console.error('Error al eliminar mensaje:', err);
            if (showErrorToast) {
                showErrorToast('Error de conexión al eliminar el mensaje.');
            }
        }
    };

    const handleOpenCreateCategory = () => {
        setEditingMessage({
            idMensaje: 0,
            vClaveMensaje: '',
            vMensajePersonalizado: '',
            idUsuario: ''
        });
    };

    return (
        <div className="se-card-panel mb-4">
            {/* Header con botón "+ Nueva Categoría" */}
            <AutomaticMessagesHeader
                totalCount={groupedCategories.length}
                onNewCategory={handleOpenCreateCategory}
                isDark={isDark}
            />

            {/* Barra de Filtros y Búsqueda */}
            <AutomaticMessagesFilters
                searchTerm={searchTerm}
                onSearchChange={setSearchTerm}
                filterType={filterType}
                onFilterChange={setFilterType}
                totalCount={groupedCategories.length}
                generalCount={countGeneral}
                personalCount={countPersonal}
                isDark={isDark}
            />

            {/* Contenido Principal: Lista de Categorías con Mensajes */}
            {isLoading ? (
                <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status" style={{ width: '2rem', height: '2rem' }} />
                    <p className="small mt-2 mb-0" style={{ color: isDark ? '#94a3b8' : '#64748b' }}>
                        Cargando mensajes automáticos...
                    </p>
                </div>
            ) : filteredCategories.length === 0 ? (
                <div
                    className="text-center py-5 rounded-3 border border-dashed"
                    style={{
                        backgroundColor: isDark ? '#121e2d' : '#f8fafc',
                        borderColor: isDark ? '#233142' : '#cbd5e1'
                    }}
                >
                    <div
                        className="mx-auto rounded-circle d-flex align-items-center justify-content-center mb-2"
                        style={{
                            width: '48px',
                            height: '48px',
                            backgroundColor: isDark ? '#182635' : '#e2e8f0',
                            color: isDark ? '#94a3b8' : '#64748b'
                        }}
                    >
                        <FontAwesomeIcon icon={faInfoCircle} style={{ fontSize: '1.2rem' }} />
                    </div>
                    <h6 className="fw-bold mb-1" style={{ color: isDark ? '#f9fafd' : '#0f172a' }}>
                        {searchTerm ? 'No se encontraron resultados' : 'No hay categorías en esta vista'}
                    </h6>
                    <p className="small mb-3" style={{ maxWidth: '420px', margin: '0 auto', color: isDark ? '#94a3b8' : '#64748b' }}>
                        {searchTerm
                            ? 'Intenta con otro término o limpia el buscador para ver todas las categorías.'
                            : 'Crea una nueva categoría o mensaje automático para comenzar a personalizar las respuestas.'}
                    </p>
                    {!searchTerm && (
                        <button
                            type="button"
                            className="btn btn-sm btn-primary rounded-pill px-3 fw-semibold shadow-none"
                            onClick={handleOpenCreateCategory}
                        >
                            <FontAwesomeIcon icon={faPlus} className="me-1" />
                            Nueva Categoría
                        </button>
                    )}
                </div>
            ) : (
                <div className="d-flex flex-column gap-3">
                    {filteredCategories.map(cat => (
                        <AutomaticMessageCard
                            key={cat.clave || cat.vClaveMensaje}
                            category={cat}
                            token={token}
                            onPreview={setSelectedMessageForPreview}
                            onEdit={categoryData => setEditingMessage(categoryData)}
                            onDelete={handleDeleteMessage}
                            onCopy={handleCopyText}
                            isCopied={copiedId}
                            isDark={isDark}
                        />
                    ))}
                </div>
            )}

            {/* Modal Crear / Editar Mensaje y Categoría */}
            {editingMessage && (
                <AutomaticMessageModal
                    editingMessage={editingMessage}
                    setEditingMessage={setEditingMessage}
                    agents={agents}
                    allMessages={messages}
                    isSaving={isSaving}
                    onSave={handleSaveMessage}
                    onClose={() => setEditingMessage(null)}
                    isDark={isDark}
                />
            )}

            {/* Modal Vista Previa de WhatsApp */}
            {selectedMessageForPreview && (
                <AutomaticMessagePreviewModal
                    message={selectedMessageForPreview}
                    onClose={() => setSelectedMessageForPreview(null)}
                    isDark={isDark}
                />
            )}
        </div>
    );
};

export default AutomaticMessagesSettingsSection;
